/**
 * Section components for the dynamic homepage builder.
 * Each section reads `cfg` from admin and renders accordingly.
 */
import { Link } from 'react-router-dom';
import { ArrowRight, Upload, Palette, ShoppingBag, Star, ChevronRight, ExternalLink, Gift, Truck, Shield, Heart, Sparkles, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import HeroOverlays from './HeroOverlays';
import { formatPriceLabel, getProductOldPriceLabel, getProductDiscountPercent } from '../../utils/pricing';
import { resolveImg } from './imageUtils';

export { resolveImg }; // re-export so existing consumers (admin previews etc.) keep working

// ─────────────────── Hero ───────────────────

// ─────────────────── Shared layout helpers ───────────────────
// height = vertical SIZE of the section (min-height of the viewport)
// padding = breathing room ABOVE and BELOW the content (independent of height)
const HEIGHT_CLASSES = {
  small: 'min-h-[35vh]',
  medium: 'min-h-[55vh]',
  large: 'min-h-[75vh]',
  full: 'min-h-[calc(100vh-80px)]',
};

const PADDING_CLASSES = {
  none: 'py-0',
  small: 'py-6',
  medium: 'py-12',
  large: 'py-20',
};

const ICONS = { Upload, Palette, ShoppingBag, Gift, Truck, Shield, Heart, Sparkles, Zap };

export const sectionWrapperClass = (cfg) => {
  const h = cfg.height && HEIGHT_CLASSES[cfg.height] ? HEIGHT_CLASSES[cfg.height] : '';
  const p = cfg.padding && PADDING_CLASSES[cfg.padding] ? PADDING_CLASSES[cfg.padding] : '';
  // When a min-height is set, vertically center the content so the section
  // doesn't collapse when content is short (matches the Pixum-style hero feel).
  const flex = h ? 'flex items-center' : '';
  return `${h} ${p} ${flex}`.trim();
};

// ─────────────────── Hero ───────────────────
export const HeroSection = ({ cfg }) => {
  const heading = cfg.heading || '';
  const highlight = cfg.heading_highlight || '';
  // Split heading on first occurrence of the highlight word so we can color it
  let beforeText = heading;
  let afterText = '';
  if (highlight && heading.includes(highlight)) {
    const idx = heading.indexOf(highlight);
    beforeText = heading.slice(0, idx);
    afterText = heading.slice(idx + highlight.length);
  }

  return (
    <section
      className={`hero-section relative overflow-hidden ${sectionWrapperClass(cfg)}`}
      style={{ backgroundColor: cfg.bg_color || '#ffffff' }}
      data-testid="hero-section"
    >
      {cfg.bg_image && (
        <>
          <div className="absolute inset-0 z-0">
            <img src={resolveImg(cfg.bg_image)} alt="" className="w-full h-full object-cover" style={{ opacity: 1 - (cfg.bg_overlay_opacity ?? 0.4) }} />
          </div>
          <div className="absolute inset-0 z-[1] bg-white" style={{ opacity: cfg.bg_overlay_opacity ?? 0.4 }} />
        </>
      )}

      <div className="container-main relative z-10 w-full">
        <div className={`grid ${cfg.side_image ? 'lg:grid-cols-2' : 'lg:grid-cols-1 max-w-3xl mx-auto text-center'} gap-12 items-center`}>
          <div className="space-y-8 animate-slide-up">
            {cfg.eyebrow && (
              <span className="text-sm text-primary uppercase tracking-widest font-medium">{cfg.eyebrow}</span>
            )}
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] text-slate-900">
              {beforeText}
              {highlight && <span className="text-primary font-accent">{highlight}</span>}
              {afterText}
            </h1>
            {cfg.subheading && (
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">{cfg.subheading}</p>
            )}
            <div className="flex flex-wrap gap-4">
              {cfg.primary_cta_label && (
                <Link to={cfg.primary_cta_link || '/produkter'}>
                  <Button className="btn-primary text-base" data-testid="cta-design-now">
                    {cfg.primary_cta_label}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              )}
              {cfg.secondary_cta_label && (
                <Link to={cfg.secondary_cta_link || '/produkter'}>
                  <Button variant="outline" className="btn-outline text-base" data-testid="cta-view-products">
                    {cfg.secondary_cta_label}
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {cfg.side_image && (
            <div className="relative animate-float lg:ml-auto lg:translate-x-12 xl:translate-x-20">
              <img src={resolveImg(cfg.side_image)} alt="" className="rounded-lg shadow-float w-full max-w-xs sm:max-w-md mx-auto lg:mx-0 lg:ml-auto" />
              {cfg.badge_text && (
                <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-lg shadow-soft">
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="w-8 h-8 rounded-full bg-slate-200 border-2 border-white" />
                      ))}
                    </div>
                    <span className="text-sm text-slate-600">{cfg.badge_text}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Free-positioned overlays (text boxes / images) */}
      <HeroOverlays overlays={cfg.overlays || []} />
    </section>
  );
};

// ─────────────────── Featured Products ───────────────────
export const FeaturedProductsSection = ({ cfg, products }) => {
  if (!products || products.length === 0) return null;
  return (
    <section
      className={sectionWrapperClass(cfg)}
      style={{ backgroundColor: cfg.bg_color || '#ffffff' }}
      data-testid="featured-products-section"
    >
      <div className="container-main">
        <div className="text-center mb-12">
          {cfg.eyebrow && <span className="text-sm text-primary uppercase tracking-widest font-medium">{cfg.eyebrow}</span>}
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-slate-900">{cfg.heading || 'Just nu populärt'}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((p) => {
            const oldPriceLabel = getProductOldPriceLabel(p);
            const discountPct = getProductDiscountPercent(p);
            const badgeText = discountPct ? `-${discountPct}%` : (oldPriceLabel ? 'REA' : null);
            return (
              <Link
                key={p.product_id}
                to={`/produkt/${p.product_id}`}
                className="group bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg transition-all"
                data-testid={`featured-product-${p.product_id}`}
              >
                <div className="aspect-square bg-slate-50 overflow-hidden relative">
                  <img
                    src={resolveImg(p.images?.[0] || p.image)}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                  {badgeText && (
                    <span className="absolute top-2 left-2 bg-[#e76f51] text-white text-xs font-bold px-2 py-0.5 rounded shadow-sm">{badgeText}</span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-slate-900 truncate">{p.name}</h3>
                  <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                    <p className="text-primary font-semibold">{formatPriceLabel(p)}</p>
                    {oldPriceLabel && (
                      <span className="text-sm text-slate-400 line-through">{oldPriceLabel}</span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─────────────────── Three Steps ───────────────────
export const ThreeStepsSection = ({ cfg }) => {
  const steps = cfg.steps || [];
  return (
    <section
      className={sectionWrapperClass(cfg)}
      style={{ backgroundColor: cfg.bg_color || '#f8fafc' }}
      data-testid="how-it-works-section"
    >
      <div className="container-main">
        <div className="text-center mb-16">
          {cfg.eyebrow && <span className="text-sm text-primary uppercase tracking-widest font-medium">{cfg.eyebrow}</span>}
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-slate-900">{cfg.heading || 'Tre enkla steg'}</h2>
        </div>
        <div className={`grid md:grid-cols-${steps.length || 3} gap-8`}>
          {steps.map((step, index) => {
            const Icon = ICONS[step.icon] || Upload;
            const stableKey = step._key || `${step.icon || 'step'}-${step.title || index}`;
            return (
              <div key={stableKey} className={`text-center space-y-4 animate-slide-up stagger-${index + 1}`} data-testid={`step-${index + 1}`}>
                <div className="step-number mx-auto">{index + 1}</div>
                <Icon className="w-8 h-8 mx-auto text-slate-400" />
                <h3 className="text-xl font-semibold text-slate-900">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─────────────────── Categories ───────────────────
export const CategoriesSection = ({ cfg, categories }) => (
  <section
    className={sectionWrapperClass(cfg)}
    style={{ backgroundColor: cfg.bg_color || '#ffffff' }}
    data-testid="categories-section"
  >
    <div className="container-main">
      <div className="flex items-end justify-between mb-12">
        <div>
          {cfg.eyebrow && <span className="text-sm text-primary uppercase tracking-widest font-medium">{cfg.eyebrow}</span>}
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-slate-900">{cfg.heading || 'Populära kategorier'}</h2>
        </div>
        {cfg.show_view_all_link !== false && (
          <Link
            to="/produkter"
            className="hidden md:flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
            data-testid="view-all-products"
          >
            Visa alla
            <ChevronRight className="w-5 h-5" />
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {categories.map((category, index) => (
          <Link
            key={category.id}
            to={`/produkter/${category.id}`}
            className={`category-card aspect-[4/5] rounded-lg animate-slide-up stagger-${index + 1}`}
            data-testid={`category-${category.id}`}
          >
            <img src={resolveImg(category.image)} alt={category.name} className="w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 right-4 z-10">
              <h3 className="text-white font-semibold text-lg">{category.name}</h3>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

// ─────────────────── Reviews ───────────────────
export const ReviewsSection = ({ cfg, reviews, reviewPlatforms }) => {
  const limit = cfg.count || 3;
  return (
    <section
      className={sectionWrapperClass(cfg)}
      style={{ backgroundColor: cfg.bg_color || '#f8fafc' }}
      data-testid="reviews-section"
    >
      <div className="container-main">
        <div className="text-center mb-16">
          {cfg.eyebrow && <span className="text-sm text-primary uppercase tracking-widest font-medium">{cfg.eyebrow}</span>}
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-slate-900">{cfg.heading || 'Vad våra kunder säger'}</h2>
        </div>
        <div className={`grid md:grid-cols-${Math.min(3, limit)} gap-6`}>
          {reviews.slice(0, limit).map((review, index) => (
            <div key={review.review_id} className={`review-card animate-slide-up stagger-${index + 1}`} data-testid={`review-${index}`}>
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={`star-${i}`} className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}`} />
                ))}
              </div>
              <p className="text-slate-600 mb-4 italic">"{review.text}"</p>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-800">{review.user_name}</p>
                {review.source && review.source !== 'manual' && (
                  <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-500 rounded-full capitalize">{review.source}</span>
                )}
              </div>
            </div>
          ))}
        </div>
        {reviewPlatforms.length > 0 && (
          <div className="mt-10 text-center" data-testid="review-platforms-links">
            <p className="text-sm text-slate-500 mb-4">Läs fler recensioner eller lämna ditt omdöme:</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {reviewPlatforms.map((p) => (
                <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:border-slate-400 hover:shadow-sm transition-all">
                  <span className="w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: p.color }}>
                    {p.icon}
                  </span>
                  {p.name}
                  <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// ─────────────────── CTA Banner ───────────────────
export const CtaBannerSection = ({ cfg }) => (
  <section
    className={sectionWrapperClass(cfg)}
    style={{ backgroundColor: cfg.bg_color || '#ffffff' }}
    data-testid="cta-section"
  >
    <div className="container-main">
      <div className="rounded-2xl p-12 md:p-16 text-center text-white" style={{ backgroundColor: cfg.card_bg_color || '#2a9d8f' }}>
        <h2 className="text-3xl md:text-4xl font-semibold mb-4">{cfg.heading || 'Redo att skapa något unikt?'}</h2>
        {cfg.subheading && <p className="text-white/80 mb-8 max-w-2xl mx-auto">{cfg.subheading}</p>}
        {cfg.cta_label && (
          <Link to={cfg.cta_link || '/produkter'}>
            <Button className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-3 font-medium text-base" data-testid="cta-bottom">
              {cfg.cta_label}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  </section>
);

// ─────────────────── Text Block ───────────────────
export const TextBlockSection = ({ cfg }) => (
  <section
    className={sectionWrapperClass(cfg)}
    style={{ backgroundColor: cfg.bg_color || '#ffffff' }}
    data-testid="text-block-section"
  >
    <div className="container-main">
      <div className={`${cfg.alignment === 'center' ? 'text-center mx-auto' : ''} max-w-3xl`}>
        {cfg.eyebrow && <span className="text-sm text-primary uppercase tracking-widest font-medium">{cfg.eyebrow}</span>}
        {cfg.heading && <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-4 mb-6 text-slate-900">{cfg.heading}</h2>}
        {cfg.body && <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: cfg.body }} />}
      </div>
    </div>
  </section>
);

// ─────────────────── Image Banner ───────────────────
export const ImageBannerSection = ({ cfg }) => (
  <section
    className={sectionWrapperClass(cfg)}
    style={{ backgroundColor: cfg.bg_color || '#ffffff' }}
    data-testid="image-banner-section"
  >
    <div className="container-main">
      <Link to={cfg.link || '/produkter'} className="block relative rounded-2xl overflow-hidden group">
        <img src={resolveImg(cfg.image)} alt={cfg.heading || ''} className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700" />
        {(cfg.heading || cfg.subheading) && (
          <div className="absolute inset-0 flex flex-col justify-center p-12 bg-gradient-to-r from-black/60 to-transparent">
            {cfg.heading && <h2 className="text-3xl md:text-5xl text-white font-bold max-w-md">{cfg.heading}</h2>}
            {cfg.subheading && <p className="text-white/90 mt-3 max-w-md">{cfg.subheading}</p>}
          </div>
        )}
      </Link>
    </div>
  </section>
);
